import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { LoadingSpinner } from '@/components/loading-spinner'

export const Route = createFileRoute('/callback')({
  component: CallbackPage,
})

function CallbackPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const auth = useAuth()
  // Ref to prevent double navigation in React Strict Mode
  const hasNavigated = useRef(false)

  useEffect(() => {
    // Prevent double execution
    if (hasNavigated.current) {
      return
    }

    // Wait for OIDC to finish processing
    if (auth.isLoading) {
      return
    }

    // Check for errors
    if (auth.error) {
      console.error('[Callback] OAuth error:', auth.error)
      // Navigate to home after a delay (user will be prompted to login)
      setTimeout(() => {
        hasNavigated.current = true
        navigate({ to: '/' })
      }, 3000)
      return
    }

    // If authenticated, redirect to home
    if (auth.isAuthenticated) {
      console.log('[Callback] OAuth flow completed successfully')
      hasNavigated.current = true
      navigate({ to: '/' })
      return
    }

    // If not authenticated and no error, something went wrong
    // This can happen if the callback page is accessed directly
    console.warn('[Callback] Unexpected state - not authenticated and no error')
    hasNavigated.current = true
    navigate({ to: '/' })
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate])

  // Show error state
  if (auth.error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">
            {t('auth.callback.title')}
          </h1>
          <p className="mb-4 text-muted-foreground">{auth.error.message}</p>
          <p className="text-sm text-muted-foreground">
            {t('auth.callback.redirecting')}
          </p>
        </div>
      </div>
    )
  }

  // Show loading state
  return (
    <LoadingSpinner
      variant="fullscreen"
      label={t('auth.callback.completing')}
    />
  )
}
